// API service for FWO Pilot Portal
import heic2any from 'heic2any'

interface ApiConfiguration {
  baseUrl: string
  region: string
  environment: 'development' | 'production'
}

interface ConfigurationValidationResult {
  isValid: boolean
  error?: {
    code: 'CONFIG_ERROR'
    message: string
    details?: any
    retryable: boolean
  }
}

export interface ApiResponse<T> {
  data?: T
  error?: {
    code: 'NETWORK_ERROR' | 'CORS_ERROR' | 'AUTH_ERROR' | 'CONFIG_ERROR' | 'API_ERROR'
    message: string
    details?: any
    retryable: boolean
  }
}

export class ApiService {
  private config: ApiConfiguration
  private authToken: string | null = null
  private configurationValid: boolean = false
  
  // Retry configuration
  private readonly maxRetryAttempts: number = 3
  private readonly baseRetryDelay: number = 1000 // 1 second
  private readonly maxRetryDelay: number = 10000 // 10 seconds
  private readonly retryTimeoutMs: number = 30000 // 30 seconds total timeout

  constructor() {
    this.config = this.initializeConfiguration()
    const validationResult = this.validateConfiguration()
    this.configurationValid = validationResult.isValid
    
    if (!validationResult.isValid) {
      console.error('API Service Configuration Error:', validationResult.error?.message)
    }

    // Initialize with stored token if available
    this.initializeStoredToken()
  }

  /**
   * Get the base URL for the API
   */
  getBaseUrl(): string {
    return this.config.baseUrl
  }

  /**
   * Initialize API service with stored authentication token
   */
  private initializeStoredToken(): void {
    // Only initialize on client side
    if (typeof window === 'undefined') {
      return
    }

    try {
      const storedToken = this.getStoredAuthToken()
      if (storedToken && this.isTokenValid(storedToken)) {
        console.log('[ApiService] Initializing with stored token')
        this.setAuthToken(storedToken)
      } else if (storedToken) {
        console.log('[ApiService] Stored token is invalid, clearing it')
        this.clearStoredAuthToken()
      }
    } catch (error) {
      console.error('[ApiService] Error initializing stored token:', error)
    }
  }

  private initializeConfiguration(): ApiConfiguration {
    const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL
    const region = import.meta.env.VITE_AWS_REGION || 'eu-west-2'
    const nodeEnv = import.meta.env.MODE || 'development'
    
    // Determine environment and fallback behavior
    const environment = nodeEnv === 'production' ? 'production' : 'development'
    
    let baseUrl: string
    
    if (apiUrl && apiUrl !== 'https://api.example.com') {
      baseUrl = apiUrl
    } else if (environment === 'development') {
      // Development fallback - allow placeholder for local development
      baseUrl = 'https://api.example.com'
    } else {
      // Production requires valid API URL
      baseUrl = apiUrl || ''
    }

    return {
      baseUrl,
      region,
      environment
    }
  }

  private validateConfiguration(): ConfigurationValidationResult {
    const { baseUrl, environment } = this.config

    // Check if API URL is missing
    if (!baseUrl) {
      return {
        isValid: false,
        error: {
          code: 'CONFIG_ERROR',
          message: 'API endpoint is not configured. Please set VITE_API_URL environment variable.',
          details: {
            environment,
            expectedVariable: 'VITE_API_URL',
            currentValue: baseUrl || 'undefined'
          },
          retryable: false
        }
      }
    }

    // Check if using placeholder URL in production
    if (environment === 'production' && baseUrl === 'https://api.example.com') {
      return {
        isValid: false,
        error: {
          code: 'CONFIG_ERROR',
          message: 'Production environment cannot use placeholder API URL. Please configure VITE_API_URL with the actual API Gateway endpoint.',
          details: {
            environment,
            currentValue: baseUrl,
            troubleshooting: 'Run the environment extraction script to get the correct API Gateway URL from Terragrunt outputs.'
          },
          retryable: false
        }
      }
    }

    // Validate URL format
    try {
      const url = new URL(baseUrl)
      if (!['http:', 'https:'].includes(url.protocol)) {
        return {
          isValid: false,
          error: {
            code: 'CONFIG_ERROR',
            message: 'Invalid API URL format. URL must use HTTP or HTTPS protocol.',
            details: {
              currentValue: baseUrl,
              protocol: url.protocol
            },
            retryable: false
          }
        }
      }
    } catch (error) {
      return {
        isValid: false,
        error: {
          code: 'CONFIG_ERROR',
          message: 'Invalid API URL format. Please provide a valid URL.',
          details: {
            currentValue: baseUrl,
            parseError: error instanceof Error ? error.message : 'Unknown parsing error'
          },
          retryable: false
        }
      }
    }

    // Warn about development placeholder but allow it
    if (environment === 'development' && baseUrl === 'https://api.example.com') {
      console.warn('API Service: Using placeholder URL in development. Set VITE_API_URL to connect to actual API Gateway.')
    }

    return { isValid: true }
  }

  public getConfiguration(): ApiConfiguration {
    return { ...this.config }
  }

  public isConfigurationValid(): boolean {
    return this.configurationValid
  }

  public getConfigurationError(): ApiResponse<never> | null {
    if (this.configurationValid) {
      return null
    }

    const validationResult = this.validateConfiguration()
    if (validationResult.error) {
      return { error: validationResult.error }
    }

    return null
  }

  setAuthToken(token: string | null) {
    this.authToken = token
  }

  getAuthToken(): string | null {
    return this.authToken
  }

  /**
   * Gets the stored authentication token from localStorage
   * @returns stored auth token or null
   */
  getStoredAuthToken(): string | null {
    // Only access localStorage on client side
    if (typeof window === 'undefined') {
      return null
    }

    try {
      const stored = localStorage.getItem('fwo_auth_session')
      if (!stored) return null
      
      const session = JSON.parse(stored)
      // Use ID token for API Gateway authorization, not access token
      return session.idToken || null
    } catch (error) {
      console.error('Error getting stored auth token:', error)
      return null
    }
  }

  /**
   * Clears the stored authentication token from localStorage
   */
  clearStoredAuthToken(): void {
    // Only access localStorage on client side
    if (typeof window === 'undefined') {
      return
    }

    try {
      localStorage.removeItem('fwo_auth_session')
      this.setAuthToken(null)
    } catch (error) {
      console.error('Error clearing stored auth token:', error)
    }
  }

  /**
   * Validates if the current token is still valid by checking its expiration
   * @param token JWT token to validate
   * @returns boolean indicating if token is valid
   */
  isTokenValid(token: string | null): boolean {
    if (!token) return false

    try {
      // Parse JWT token to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Math.floor(Date.now() / 1000)
      
      // Check if token has expired (with 5 minute buffer)
      return payload.exp && payload.exp > (currentTime + 300)
    } catch (error) {
      console.error('Error validating token:', error)
      return false
    }
  }

  /**
   * Checks if token needs refresh (expires within 5 minutes)
   * @param token JWT token to check
   * @returns boolean indicating if token needs refresh
   */
  private needsRefresh(token: string | null): boolean {
    if (!token) return false

    try {
      // Parse JWT token to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Math.floor(Date.now() / 1000)
      
      // Check if token expires within 5 minutes (300 seconds)
      return payload.exp && payload.exp <= (currentTime + 300)
    } catch (error) {
      console.error('Error checking token expiration:', error)
      return false
    }
  }

  /**
   * Gets the stored refresh token from localStorage
   * @returns refresh token or null
   */
  private getStoredRefreshToken(): string | null {
    // Only access localStorage on client side
    if (typeof window === 'undefined') {
      return null
    }

    try {
      const stored = localStorage.getItem('fwo_auth_session')
      if (!stored) return null
      
      const session = JSON.parse(stored)
      return session.refreshToken || null
    } catch (error) {
      console.error('Error getting stored refresh token:', error)
      return null
    }
  }

  /**
   * Updates stored session with new tokens
   * @param newTokens New authentication tokens
   */
  private updateStoredTokens(newTokens: { accessToken: string; idToken?: string; refreshToken?: string; expiresIn: number }): void {
    // Only access localStorage on client side
    if (typeof window === 'undefined') {
      return
    }

    try {
      const stored = localStorage.getItem('fwo_auth_session')
      if (!stored) return
      
      const session = JSON.parse(stored)
      const expiresAt = Math.floor(Date.now() / 1000) + newTokens.expiresIn
      
      // Update session with new tokens
      session.accessToken = newTokens.accessToken
      if (newTokens.idToken) {
        session.idToken = newTokens.idToken
      }
      if (newTokens.refreshToken) {
        session.refreshToken = newTokens.refreshToken
      }
      session.expiresAt = expiresAt
      
      localStorage.setItem('fwo_auth_session', JSON.stringify(session))
      // Use ID token for API Gateway authorization
      this.setAuthToken(newTokens.idToken || newTokens.accessToken)
    } catch (error) {
      console.error('Error updating stored tokens:', error)
    }
  }

  /**
   * Clears stored authentication session
   */
  private clearStoredSession(): void {
    // Only access localStorage on client side
    if (typeof window === 'undefined') {
      return
    }

    localStorage.removeItem('fwo_auth_session')
    this.setAuthToken(null)
  }

  /**
   * Attempts to refresh the authentication token using stored refresh token
   * @returns Promise with new tokens or error
   */
  async refreshToken(): Promise<ApiResponse<{ accessToken: string; idToken?: string; refreshToken?: string; expiresIn: number }>> {
    const refreshToken = this.getStoredRefreshToken()
    
    if (!refreshToken) {
      console.log('[ApiService] No refresh token available')
      return {
        error: {
          code: 'AUTH_ERROR',
          message: 'No refresh token available',
          retryable: false
        }
      }
    }

    try {
      console.log('[ApiService] Attempting token refresh...')
      // Make refresh request without going through the interceptor to avoid infinite loops
      const response = await this.performSingleRequest<{ accessToken: string; refreshToken?: string; expiresIn: number }>('/auth/refresh-token', {
        method: 'POST',
        body: JSON.stringify({ refreshToken })
      })

      if (response.data) {
        console.log('[ApiService] Token refresh successful')
        // The backend response might be wrapped, so handle both formats
        const tokenData = (response.data as any)?.data || response.data
        
        // Update stored tokens with new ones
        this.updateStoredTokens({
          accessToken: tokenData.accessToken,
          idToken: tokenData.idToken,
          refreshToken: tokenData.refreshToken || refreshToken,
          expiresIn: tokenData.expiresIn || 3600 // Default to 1 hour if not provided
        })
        
        return { data: tokenData }
      }

      console.log('[ApiService] Token refresh failed - no data in response')
      return response
    } catch (error) {
      console.error('[ApiService] Token refresh error:', error)
      return {
        error: {
          code: 'AUTH_ERROR',
          message: 'Token refresh failed',
          retryable: false
        }
      }
    }
  }

  /**
   * Token refresh interceptor - handles automatic token refresh before requests
   * @returns Promise that resolves when token is valid or refresh is complete
   */
  private async handleTokenRefresh(): Promise<{ success: boolean; shouldRedirectToLogin: boolean }> {
    // If no token, no need to refresh
    if (!this.authToken) {
      return { success: true, shouldRedirectToLogin: false }
    }

    // If token is still valid (not expiring within 5 minutes), continue
    if (!this.needsRefresh(this.authToken)) {
      return { success: true, shouldRedirectToLogin: false }
    }

    console.log('Token expires within 5 minutes, attempting refresh...')

    try {
      const refreshResponse = await this.refreshToken()
      
      if (refreshResponse.data) {
        console.log('Token refreshed successfully')
        return { success: true, shouldRedirectToLogin: false }
      } else {
        console.error('Token refresh failed:', refreshResponse.error?.message)
        // Clear stored session on refresh failure
        this.clearStoredSession()
        return { success: false, shouldRedirectToLogin: true }
      }
    } catch (error) {
      console.error('Token refresh error:', error)
      // Clear stored session on refresh error
      this.clearStoredSession()
      return { success: false, shouldRedirectToLogin: true }
    }
  }

  private handleNetworkError(error: Error): ApiResponse<never> {
    // Determine if this is a network connectivity issue
    const isNetworkError = error.name === 'TypeError' || 
                          error.message.includes('Failed to fetch') ||
                          error.message.includes('Network request failed') ||
                          error.message.includes('ERR_NETWORK') ||
                          error.message.includes('ERR_INTERNET_DISCONNECTED')

    if (isNetworkError) {
      return {
        error: {
          code: 'NETWORK_ERROR',
          message: 'Unable to connect to the server. Please check your internet connection and try again.',
          details: {
            originalError: error.message,
            troubleshooting: [
              'Check your internet connection',
              'Try refreshing the page',
              'Contact support if the problem persists'
            ]
          },
          retryable: true
        }
      }
    }

    // Handle DNS resolution errors
    if (error.message.includes('ERR_NAME_NOT_RESOLVED') || 
        error.message.includes('getaddrinfo ENOTFOUND')) {
      return {
        error: {
          code: 'NETWORK_ERROR',
          message: 'Cannot reach the API server. The server address may be incorrect or the server may be down.',
          details: {
            originalError: error.message,
            apiUrl: this.config.baseUrl,
            troubleshooting: [
              'Verify the API endpoint configuration',
              'Check if the server is running',
              'Contact your system administrator'
            ]
          },
          retryable: false
        }
      }
    }

    // Handle timeout errors
    if (error.message.includes('timeout') || error.message.includes('ETIMEDOUT')) {
      return {
        error: {
          code: 'NETWORK_ERROR',
          message: 'Request timed out. The server is taking too long to respond.',
          details: {
            originalError: error.message,
            troubleshooting: [
              'Try again in a few moments',
              'Check your internet connection speed',
              'Contact support if timeouts persist'
            ]
          },
          retryable: true
        }
      }
    }

    // Generic network error fallback
    return {
      error: {
        code: 'NETWORK_ERROR',
        message: 'A network error occurred while trying to connect to the server.',
        details: {
          originalError: error.message,
          troubleshooting: [
            'Check your internet connection',
            'Try refreshing the page',
            'Contact support if the problem persists'
          ]
        },
        retryable: true
      }
    }
  }

  private handleCorsError(response: Response): ApiResponse<never> {
    // CORS errors typically manifest as opaque responses or specific status codes
    const isCorsError = response.type === 'opaque' || 
                       response.status === 0 ||
                       (response.status === 403 && response.statusText === '')

    if (isCorsError) {
      return {
        error: {
          code: 'CORS_ERROR',
          message: 'Cross-origin request blocked. The API server may not be configured to accept requests from this domain.',
          details: {
            responseType: response.type,
            status: response.status,
            statusText: response.statusText,
            requestOrigin: window.location.origin,
            apiUrl: this.config.baseUrl,
            troubleshooting: [
              'Verify CORS configuration on the API Gateway',
              'Check if the frontend domain is whitelisted',
              'Contact your system administrator'
            ]
          },
          retryable: false
        }
      }
    }

    // Handle preflight request failures
    if (response.status === 405 && response.statusText.includes('Method Not Allowed')) {
      return {
        error: {
          code: 'CORS_ERROR',
          message: 'CORS preflight request failed. The API server does not allow the requested method from this origin.',
          details: {
            status: response.status,
            statusText: response.statusText,
            troubleshooting: [
              'Check API Gateway CORS configuration',
              'Verify allowed methods include OPTIONS',
              'Contact your system administrator'
            ]
          },
          retryable: false
        }
      }
    }

    // Not a CORS error, return null to indicate other error handling should proceed
    return {
      error: {
        code: 'API_ERROR',
        message: 'An unexpected response was received from the server.',
        details: {
          status: response.status,
          statusText: response.statusText
        },
        retryable: false
      }
    }
  }

  /**
   * Retry mechanism with exponential backoff for transient network failures
   * @param requestFn Function that returns a Promise to retry
   * @param maxRetries Maximum number of retry attempts (default: configured maxRetryAttempts)
   * @returns Promise that resolves with the successful result or rejects with the final error
   */
  private async retryRequest<T>(
    requestFn: () => Promise<ApiResponse<T>>,
    maxRetries: number = this.maxRetryAttempts
  ): Promise<ApiResponse<T>> {
    let lastError: ApiResponse<T> | null = null
    const startTime = Date.now()

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      // Check if we've exceeded the total timeout
      if (Date.now() - startTime > this.retryTimeoutMs) {
        return {
          error: {
            code: 'NETWORK_ERROR',
            message: 'Request timed out after multiple retry attempts.',
            details: {
              totalAttempts: attempt,
              totalTimeMs: Date.now() - startTime,
              maxTimeoutMs: this.retryTimeoutMs,
              troubleshooting: [
                'Check your internet connection',
                'Try again later when network conditions improve',
                'Contact support if the problem persists'
              ]
            },
            retryable: false
          }
        }
      }

      try {
        const result = await requestFn()
        
        // If the request succeeded, return the result
        if (!result.error) {
          if (attempt > 0) {
            console.log(`API request succeeded after ${attempt} retry attempts`)
          }
          return result
        }

        // If the error is not retryable, return immediately
        if (!result.error.retryable) {
          return result
        }

        // Store the error for potential return
        lastError = result

        // If this is the last attempt, don't wait
        if (attempt === maxRetries) {
          break
        }

        // Calculate delay with exponential backoff and jitter
        const baseDelay = Math.min(
          this.baseRetryDelay * Math.pow(2, attempt),
          this.maxRetryDelay
        )
        
        // Add jitter (±25% of the base delay) to prevent thundering herd
        const jitter = baseDelay * 0.25 * (Math.random() * 2 - 1)
        const delay = Math.max(0, baseDelay + jitter)

        console.log(`API request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${Math.round(delay)}ms...`, result.error)

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay))

      } catch (error) {
        // This shouldn't happen since requestFn should return ApiResponse, but handle it just in case
        lastError = {
          error: {
            code: 'NETWORK_ERROR',
            message: 'Unexpected error during retry attempt',
            details: {
              originalError: error instanceof Error ? error.message : 'Unknown error',
              attempt: attempt + 1
            },
            retryable: false
          }
        }
        
        if (attempt === maxRetries) {
          break
        }
      }
    }

    // Return the last error with updated details about retry attempts
    if (lastError?.error) {
      return {
        error: {
          ...lastError.error,
          message: `${lastError.error.message} (Failed after ${maxRetries + 1} attempts)`,
          details: {
            ...lastError.error.details,
            totalAttempts: maxRetries + 1,
            totalTimeMs: Date.now() - startTime,
            retryConfiguration: {
              maxRetryAttempts: this.maxRetryAttempts,
              baseRetryDelay: this.baseRetryDelay,
              maxRetryDelay: this.maxRetryDelay,
              retryTimeoutMs: this.retryTimeoutMs
            }
          }
        }
      }
    }

    // Fallback error (should not reach here)
    return {
      error: {
        code: 'NETWORK_ERROR',
        message: 'Request failed after multiple retry attempts',
        details: {
          totalAttempts: maxRetries + 1,
          totalTimeMs: Date.now() - startTime
        },
        retryable: false
      }
    }
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    // Check configuration before making requests
    if (!this.configurationValid) {
      const configError = this.getConfigurationError()
      if (configError?.error) {
        return { error: configError.error }
      }
    }

    // Skip token refresh for auth endpoints to avoid infinite loops
    const isAuthEndpoint = endpoint.startsWith('/auth/')
    
    if (!isAuthEndpoint) {
      // Ensure we have the latest token from storage
      if (!this.authToken) {
        const storedToken = this.getStoredAuthToken()
        if (storedToken && this.isTokenValid(storedToken)) {
          console.log('[ApiService] Using stored token for request')
          this.setAuthToken(storedToken)
        }
      }

      // Handle token refresh before making the request
      const refreshResult = await this.handleTokenRefresh()
      
      if (!refreshResult.success) {
        if (refreshResult.shouldRedirectToLogin) {
          // Dispatch custom event to notify components about auth failure
          window.dispatchEvent(new CustomEvent('auth:session-expired'))
          
          return {
            error: {
              code: 'AUTH_ERROR',
              message: 'Your session has expired. Please log in again.',
              retryable: false
            }
          }
        }
      }
    }

    // Use retry mechanism for the actual request with 401 handling
    return this.retryRequest(async () => {
      const response = await this.performSingleRequest<T>(endpoint, options)
      
      // Handle 401 responses by attempting token refresh
      if (response.error?.code === 'AUTH_ERROR' && !isAuthEndpoint) {
        console.log('[ApiService] Received 401, attempting token refresh...')
        
        const refreshResult = await this.handleTokenRefresh()
        if (refreshResult.success) {
          console.log('[ApiService] Token refreshed, retrying request...')
          // Retry the request with the new token
          return this.performSingleRequest<T>(endpoint, options)
        } else {
          console.log('[ApiService] Token refresh failed, session expired')
          // Dispatch session expired event
          window.dispatchEvent(new CustomEvent('auth:session-expired'))
        }
      }
      
      return response
    })
  }

  /**
   * Performs a single HTTP request without retry logic
   * @param endpoint API endpoint path
   * @param options Fetch options
   * @returns Promise with ApiResponse
   */
  private async performSingleRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.config.baseUrl}${endpoint}`
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      // Check for CORS errors first (before trying to parse JSON)
      if (response.type === 'opaque' || response.status === 0) {
        return this.handleCorsError(response)
      }

      // Handle specific HTTP status codes with enhanced error messages
      if (!response.ok) {
        // Try to parse error response, but handle cases where it fails
        let errorData: any = {}
        try {
          errorData = await response.json()
        } catch {
          // If we can't parse the response, it might be a CORS issue
          if (response.status === 403 || response.status === 405) {
            return this.handleCorsError(response)
          }
        }

        // Handle authentication errors
        if (response.status === 401) {
          return {
            error: {
              code: 'AUTH_ERROR',
              message: 'Authentication failed. Please check your credentials and try again.',
              details: {
                status: response.status,
                statusText: response.statusText,
                originalError: errorData.error?.message,
                troubleshooting: [
                  'Verify your email and password are correct',
                  'Request new login credentials if needed',
                  'Contact support if the problem persists'
                ]
              },
              retryable: false
            }
          }
        }

        // Handle forbidden errors
        if (response.status === 403) {
          return {
            error: {
              code: 'AUTH_ERROR',
              message: 'Access denied. You do not have permission to perform this action.',
              details: {
                status: response.status,
                statusText: response.statusText,
                originalError: errorData.error?.message,
                troubleshooting: [
                  'Ensure you are logged in',
                  'Check if your account has the required permissions',
                  'Contact an administrator if needed'
                ]
              },
              retryable: false
            }
          }
        }

        // Handle rate limiting
        if (response.status === 429) {
          return {
            error: {
              code: 'API_ERROR',
              message: 'Too many requests. Please wait a moment before trying again.',
              details: {
                status: response.status,
                statusText: response.statusText,
                retryAfter: response.headers.get('Retry-After'),
                troubleshooting: [
                  'Wait a few seconds before retrying',
                  'Reduce the frequency of your requests'
                ]
              },
              retryable: true
            }
          }
        }

        // Handle server errors
        if (response.status >= 500) {
          return {
            error: {
              code: 'API_ERROR',
              message: 'Server error occurred. Please try again later.',
              details: {
                status: response.status,
                statusText: response.statusText,
                originalError: errorData.error?.message,
                troubleshooting: [
                  'Try again in a few moments',
                  'Contact support if the problem persists'
                ]
              },
              retryable: true
            }
          }
        }

        // Handle client errors (4xx)
        return {
          error: {
            code: 'API_ERROR',
            message: errorData.error?.message || 'Request failed. Please check your input and try again.',
            details: {
              status: response.status,
              statusText: response.statusText,
              originalError: errorData.error?.details,
              troubleshooting: [
                'Check your input data',
                'Ensure all required fields are provided',
                'Contact support if the error persists'
              ]
            },
            retryable: false
          }
        }
      }

      // Parse successful response
      let data: T
      try {
        data = await response.json()
      } catch (parseError) {
        return {
          error: {
            code: 'API_ERROR',
            message: 'Invalid response format received from server.',
            details: {
              parseError: parseError instanceof Error ? parseError.message : 'Unknown parsing error',
              troubleshooting: [
                'Try refreshing the page',
                'Contact support if the problem persists'
              ]
            },
            retryable: true
          }
        }
      }

      return { data }
    } catch (error) {
      // Handle network and other fetch errors
      return this.handleNetworkError(error as Error)
    }
  }

  // Auth endpoints
  async sendLoginCredentials(email: string): Promise<ApiResponse<{ message: string }>> {
    return this.request('/auth/send-credentials', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  async login(email: string, password: string, session?: string, newPassword?: string): Promise<ApiResponse<{
    accessToken?: string;
    idToken?: string;
    refreshToken?: string;
    expiresIn?: number;
    challengeName?: string;
    session?: string;
    user?: {
      userId: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
      requiresPasswordChange: boolean;
    };
  }>> {
    const body: any = { email, password }
    
    // Handle NEW_PASSWORD_REQUIRED challenge response
    if (session && newPassword) {
      body.session = session
      body.newPassword = newPassword
    }
    
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async changePassword(email: string, session: string, newPassword: string): Promise<ApiResponse<{
    accessToken: string;
    idToken: string;
    refreshToken: string;
    expiresIn: number;
    user: {
      userId: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
      requiresPasswordChange: boolean;
    };
  }>> {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ email, session, newPassword }),
    })
  }

  async logout(): Promise<ApiResponse<{ message: string }>> {
    return this.request('/auth/logout', {
      method: 'POST',
    })
  }

  async resetPassword(email: string): Promise<ApiResponse<{ message: string }>> {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  // User endpoints
  async getUserProfile() {
    return this.request('/auth/profile', {
      method: 'GET',
    })
  }

  async getUserById(userId: string) {
    return this.request(`/users/${userId}`, {
      method: 'GET',
    })
  }

  async updateUserProfile(profile: Partial<any>) {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profile),
    })
  }

  async updateCookieConsent(consent: any) {
    return this.request('/users/cookie-consent', {
      method: 'PUT',
      body: JSON.stringify(consent),
    })
  }

  // Events endpoints
  async getEvents() {
    return this.request('/events', {
      method: 'GET',
    })
  }

  async signupForEvent(eventId: string) {
    return this.request(`/events/${eventId}/signup`, {
      method: 'POST',
    })
  }

  async withdrawFromEvent(eventId: string) {
    return this.request(`/events/${eventId}/withdraw`, {
      method: 'DELETE',
    })
  }

  // Photo competition endpoints
  async getCurrentThemes() {
    return this.request('/photos/themes', {
      method: 'GET',
    })
  }

  // Admin: get all themes across all dates
  async getAllThemes() {
    return this.request('/photos/themes?all=true', {
      method: 'GET',
    })
  }

  async uploadPhoto(themeId: string, file: File, caption?: string): Promise<ApiResponse<any>> {
    // Resolve MIME type — iOS sometimes reports empty or generic types for HEIC
    const mimeType = this.resolveFileMimeType(file)

    // Convert HEIC/HEIF to JPEG client-side (server-side sharp doesn't support HEIC decoding)
    let uploadFile = file
    let uploadMimeType = mimeType
    let uploadFileName = file.name

    if (mimeType === 'image/heic' || mimeType === 'image/heif') {
      try {
        const jpegBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.92
        })
        // heic2any may return a single blob or an array
        const resultBlob = Array.isArray(jpegBlob) ? jpegBlob[0] : jpegBlob
        // Create a new File with .jpg extension
        uploadFileName = file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg')
        uploadFile = new File([resultBlob], uploadFileName, { type: 'image/jpeg' })
        uploadMimeType = 'image/jpeg'
      } catch (conversionError) {
        console.error('HEIC conversion failed:', conversionError)
        return {
          error: {
            code: 'API_ERROR' as const,
            message: 'Failed to convert HEIC image. Please try converting the image to JPG before uploading.',
            details: { error: conversionError instanceof Error ? conversionError.message : 'Unknown conversion error' },
            retryable: false
          }
        }
      }
    }

    // Step 1: Request a presigned S3 upload URL from the backend
    const urlResponse = await this.request<{
      uploadUrl: string
      submissionId: string
      s3Key: string
      expiresIn: number
    }>('/photos/request-upload-url', {
      method: 'POST',
      body: JSON.stringify({
        themeId,
        fileName: uploadFileName,
        fileSize: uploadFile.size,
        mimeType: uploadMimeType,
        caption
      }),
    })

    if (urlResponse.error) {
      return urlResponse
    }

    if (!urlResponse.data?.uploadUrl) {
      return {
        error: {
          code: 'API_ERROR' as const,
          message: 'Failed to get upload URL from server',
          details: { response: urlResponse.data },
          retryable: true
        }
      }
    }

    // Step 2: Upload the file directly to S3 using the presigned URL
    try {
      const s3Response = await fetch(urlResponse.data.uploadUrl, {
        method: 'PUT',
        body: uploadFile,
        headers: {
          'Content-Type': uploadMimeType,
        },
      })

      if (!s3Response.ok) {
        const statusText = s3Response.statusText || `HTTP ${s3Response.status}`
        return {
          error: {
            code: 'API_ERROR' as const,
            message: `Upload failed: ${statusText}. Please try again.`,
            details: { status: s3Response.status, statusText },
            retryable: s3Response.status >= 500
          }
        }
      }

      return {
        data: {
          message: 'Photo uploaded successfully',
          submission: {
            submissionId: urlResponse.data.submissionId,
            themeId,
            fileName: uploadFileName,
            fileSize: uploadFile.size,
            mimeType: uploadMimeType,
          }
        }
      }
    } catch (error) {
      // Network errors during S3 upload
      const message = error instanceof Error ? error.message : 'Unknown error'
      return {
        error: {
          code: 'NETWORK_ERROR' as const,
          message: `Upload failed due to a network issue. Please check your connection and try again.`,
          details: { error: message },
          retryable: true
        }
      }
    }
  }

  /**
   * Resolve the MIME type for a file, using extension-based fallback for iOS devices
   * that may report empty or generic MIME types for HEIC/HEIF files.
   */
  private resolveFileMimeType(file: File): string {
    const type = file.type?.toLowerCase()

    // If the browser reported a valid image MIME type, trust it
    if (type && type.startsWith('image/')) {
      return type
    }

    // Fallback: detect from file extension (common on iOS for HEIC)
    const extension = file.name.split('.').pop()?.toLowerCase()
    const extensionMimeMap: Record<string, string> = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'heic': 'image/heic',
      'heif': 'image/heif',
    }

    if (extension && extensionMimeMap[extension]) {
      return extensionMimeMap[extension]
    }

    // Last resort: assume JPEG (most common camera format)
    return 'image/jpeg'
  }

  async getPublishedPhotos(date?: string) {
    const queryParams = date ? `?date=${date}` : ''
    return this.request(`/photos/gallery${queryParams}`, {
      method: 'GET',
    })
  }

  // Membership card endpoints
  async generateMembershipCard(format: 'svg' | 'png' = 'svg') {
    const queryParams = format ? `?format=${format}` : ''
    return this.request(`/users/membership-card${queryParams}`, {
      method: 'GET',
    })
  }

  // Wallet pass endpoints
  async generateWalletPass(platform: 'apple' | 'google') {
    return this.request(`/users/membership-card/wallet?platform=${platform}`, {
      method: 'GET',
    })
  }

  // Admin - User Management endpoints
  async getAllUsers() {
    return this.request('/admin/users', {
      method: 'GET',
    })
  }

  async createUser(userData: {
    email: string;
    firstName: string;
    lastName: string;
    role: 'pilot' | 'photo_admin' | 'admin';
    emergencyContact?: {
      name?: string;
      phone?: string;
      relationship?: string;
    };
    competitionYear?: string;
  }) {
    return this.request('/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  async deleteUser(userId: string) {
    return this.request(`/admin/users/${userId}`, {
      method: 'DELETE',
    })
  }

  async deleteAllUsers() {
    return this.request('/admin/users/all', {
      method: 'DELETE',
    })
  }

  async importUsers(rows: Array<Record<string, string>>, competitionYear: string, totalDataRows: number, skippedRows: number) {
    return this.request('/admin/users/import', {
      method: 'POST',
      body: JSON.stringify({ rows, competitionYear, totalDataRows, skippedRows }),
    })
  }

  async resendInvite(userId: string) {
    return this.request(`/admin/users/${userId}/resend-invite`, {
      method: 'POST',
    })
  }

  async sendAllInvites(competitionYear?: string) {
    return this.request('/admin/users/send-all-invites', {
      method: 'POST',
      body: JSON.stringify({ competitionYear }),
    })
  }

  // Admin - Event Management endpoints
  async createEvent(event: {
    title: string;
    description: string;
    eventType: string;
    startTime: string;
    endTime: string;
    location?: string;
    maxParticipants?: number;
    signupRequired: boolean;
  }) {
    return this.request('/events', {
      method: 'POST',
      body: JSON.stringify(event),
    })
  }

  async updateEvent(eventId: string, event: Partial<{
    title: string;
    description: string;
    eventType: string;
    startTime: string;
    endTime: string;
    location?: string;
    maxParticipants?: number;
    signupRequired: boolean;
  }>) {
    return this.request(`/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(event),
    })
  }

  async deleteEvent(eventId: string) {
    return this.request(`/events/${eventId}`, {
      method: 'DELETE',
    })
  }

  // Admin - Photo Management endpoints
  async createTheme(theme: {
    date?: string;
    title: string;
    description: string;
    type?: 'pilot' | 'official';
  }) {
    return this.request('/photos/themes', {
      method: 'POST',
      body: JSON.stringify(theme),
    })
  }

  async updateTheme(themeId: string, theme: Partial<{
    title: string;
    description: string;
    isActive: boolean;
    winnerSubmissionId: string | null;
  }>) {
    return this.request(`/photos/themes/${themeId}`, {
      method: 'PUT',
      body: JSON.stringify(theme),
    })
  }

  async deleteTheme(themeId: string) {
    return this.request(`/photos/themes/${themeId}`, {
      method: 'DELETE',
    })
  }

  async getThemeSubmissions(themeId: string) {
    return this.request(`/photos/themes/${themeId}/submissions`, {
      method: 'GET',
    })
  }

  async publishSubmission(submissionId: string) {
    return this.request(`/photos/submissions/${submissionId}/publish`, {
      method: 'POST',
    })
  }

  async unpublishSubmission(submissionId: string) {
    return this.request(`/photos/submissions/${submissionId}/unpublish`, {
      method: 'POST',
    })
  }

  async batchPublishSubmissions(submissionIds: string[]) {
    return this.request('/photos/submissions/batch-publish', {
      method: 'POST',
      body: JSON.stringify({ submissionIds }),
    })
  }

  async downloadSubmissionsZip(date: string, regenerate = false) {
    const params = new URLSearchParams({ date })
    if (regenerate) params.set('regenerate', 'true')

    // First request — triggers generation or returns cached ZIP
    const response = await this.request<any>(`/photos/submissions/download?${params}`, {
      method: 'GET',
    })

    if (response.error) return response

    const payload = response.data?.data || response.data

    // ZIP is ready — return immediately
    if (payload?.status === 'ready') {
      return { data: payload }
    }

    // ZIP is generating — poll until ready (max 90 seconds)
    if (payload?.status === 'generating') {
      const maxAttempts = 18
      const pollInterval = 5000 // 5 seconds

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise(resolve => setTimeout(resolve, pollInterval))

        const pollParams = new URLSearchParams({ date, poll: 'true' })
        const pollResponse = await this.request<any>(`/photos/submissions/download?${pollParams}`, {
          method: 'GET',
        })

        if (pollResponse.error) return pollResponse

        const pollPayload = pollResponse.data?.data || pollResponse.data
        if (pollPayload?.status === 'ready') {
          return { data: pollPayload }
        }
      }

      return {
        error: {
          code: 'API_ERROR' as const,
          message: 'ZIP file is still being prepared. Please try the Download ZIP button again in a moment.',
          details: { status: 'timeout' },
          retryable: true
        }
      }
    }

    // Fallback for unexpected response shapes
    return response
  }

  // Admin - Competition Management endpoints
  async createCompetition(competition: {
    year: string;
    name: string;
    startDate: string;
    endDate: string;
    location?: string;
    description?: string;
  }) {
    return this.request('/admin/competitions', {
      method: 'POST',
      body: JSON.stringify(competition),
    })
  }

  async getCompetitions() {
    return this.request('/admin/competitions', {
      method: 'GET',
    })
  }

  async setActiveCompetition(competitionId: string) {
    return this.request(`/admin/competitions/${competitionId}/set-active`, {
      method: 'POST',
    })
  }

  async updateCompetition(competitionId: string, data: Partial<{
    year: string;
    name: string;
    startDate: string;
    endDate: string;
    location: string;
    description: string | null;
    albatrossCompId: string | null;
    settings: {
      maxParticipants?: number;
      photoThemesPerDay?: number;
      busGroupsEnabled?: boolean;
      registrationOpen?: boolean;
    };
  }>) {
    return this.request(`/admin/competitions/${competitionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // Admin - Bus Group Management endpoints
  async assignBusGroup(userId: string, busGroup: string) {
    return this.request('/admin/bus-groups/assign', {
      method: 'POST',
      body: JSON.stringify({ userId, busGroup }),
    })
  }

  async getBusGroups() {
    return this.request('/admin/bus-groups', {
      method: 'GET',
    })
  }

  async getBusGroupMembers(busGroup: string) {
    return this.request(`/admin/bus-groups/${busGroup}/members`, {
      method: 'GET',
    })
  }

  async batchUploadPhotos(themeId: string, photos: Array<{ file: File; caption?: string }>): Promise<ApiResponse<any>> {
    // Step 1: request presigned S3 upload URLs from the backend.
    // Only metadata is sent here — NOT the file bytes. Sending base64-encoded
    // file content in this request would inflate the JSON payload by ~33%,
    // which can blow past the 6MB Lambda synchronous invocation limit for
    // batches of several full-size photos (e.g. 9 photos / 6MB raw becomes
    // ~8MB base64) and cause the whole batch to fail with a generic error.
    const photoMetadata = photos.map(({ file, caption }) => ({
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      caption,
    }))

    const presignResponse = await this.request<{ uploads: Array<{ submissionId: string; uploadUrl: string }> }>(
      '/photos/upload/batch',
      {
        method: 'POST',
        body: JSON.stringify({ themeId, photos: photoMetadata }),
      }
    )

    if (presignResponse.error) {
      return presignResponse
    }

    const uploads = (presignResponse.data as any)?.data?.uploads || (presignResponse.data as any)?.uploads
    if (!Array.isArray(uploads) || uploads.length !== photos.length) {
      return {
        error: {
          code: 'API_ERROR',
          message: 'Unexpected response when requesting upload URLs',
          retryable: false,
        },
      }
    }

    // Step 2: PUT each file's raw bytes directly to its presigned S3 URL.
    const failures: string[] = []
    await Promise.all(
      photos.map(async ({ file }, index) => {
        const uploadUrl = uploads[index]?.uploadUrl
        if (!uploadUrl) {
          failures.push(file.name)
          return
        }
        try {
          const putResponse = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
          })
          if (!putResponse.ok) {
            failures.push(file.name)
          }
        } catch {
          failures.push(file.name)
        }
      })
    )

    if (failures.length > 0) {
      return {
        error: {
          code: 'API_ERROR',
          message: `Failed to upload ${failures.length} of ${photos.length} photo(s) to storage: ${failures.join(', ')}`,
          retryable: true,
        },
      }
    }

    return presignResponse
  }

  // Admin - Wallet Pass Management endpoints
  async triggerWalletPassUpdate(request: {
    userId?: string;
    competitionYear?: string;
    updateReason?: string;
  }) {
    return this.request('/admin/wallet-passes/trigger-update', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async getWalletPassStats(userId: string) {
    return this.request(`/admin/wallet-passes/stats/${userId}`, {
      method: 'GET',
    })
  }

  // Admin - Role Management
  async updateUserRole(userId: string, role: string) {
    return this.request(`/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    })
  }

  // Admin - Headshot Management endpoints
  async uploadHeadshotUrl(filename: string) {
    return this.request<{ uploadUrl: string; key: string }>('/admin/headshots/upload-url', {
      method: 'POST',
      body: JSON.stringify({ filename }),
    })
  }

  async listHeadshots() {
    return this.request<{ headshots: Array<{ key: string; thumbnailKey: string; fileName: string; uploadedAt: string; linkedUserId?: string; thumbnailUrl?: string }> }>('/admin/headshots', {
      method: 'GET',
    })
  }

  async linkHeadshot(userId: string, headshotKey: string) {
    return this.request('/admin/headshots/link', {
      method: 'POST',
      body: JSON.stringify({ userId, headshotKey }),
    })
  }

  // Admin - Team Management endpoints
  async listTeams() {
    return this.request<{ competitionYear: string; maxTeamSize: number; teams: Array<{ teamId: string; teamName: string; memberCount: number; isFull: boolean }> }>('/admin/teams', {
      method: 'GET',
    })
  }

  async createTeam(teamName: string) {
    return this.request<{ teamId: string; teamName: string; competitionYear: string; createdBy: string; createdAt: string; updatedAt: string }>('/admin/teams', {
      method: 'POST',
      body: JSON.stringify({ teamName }),
    })
  }

  async renameTeam(teamId: string, teamName: string) {
    return this.request<{ teamId: string; teamName: string; updatedAt: string }>(`/admin/teams/${teamId}`, {
      method: 'PUT',
      body: JSON.stringify({ teamName }),
    })
  }

  async deleteTeam(teamId: string) {
    return this.request<void>(`/admin/teams/${teamId}`, {
      method: 'DELETE',
    })
  }

  async listTeamMembers(teamId: string) {
    return this.request<{ members: Array<{ userId: string; firstName: string; lastName: string; gender: string; paragliderManufacturer: string; paragliderModel: string; faiNation: string }> }>(`/admin/teams/${teamId}/members`, {
      method: 'GET',
    })
  }

  async listUnassignedPilots() {
    return this.request<{ pilots: Array<{ userId: string; firstName: string; lastName: string; gender: string; paragliderManufacturer: string; paragliderModel: string; faiNation: string }> }>('/admin/teams/unassigned', {
      method: 'GET',
    })
  }

  async assignPilot(teamId: string, userId: string) {
    return this.request<{ previousTeamId: string | null }>(`/admin/teams/${teamId}/members/${userId}`, {
      method: 'PUT',
    })
  }

  async removePilot(userId: string) {
    return this.request<void>(`/admin/teams/members/${userId}`, {
      method: 'DELETE',
    })
  }

  async getTeamSettings() {
    return this.request<{ competitionYear: string; teamFeatureEnabled: boolean; maxTeamSize: number; updatedAt: string; updatedBy: string }>('/admin/teams/settings', {
      method: 'GET',
    })
  }

  async setTeamFeatureFlag(competitionYear: string, teamFeatureEnabled: boolean) {
    return this.request<{ competitionYear: string; teamFeatureEnabled: boolean; maxTeamSize: number; updatedAt: string; updatedBy: string }>('/admin/teams/settings/feature-flag', {
      method: 'PUT',
      body: JSON.stringify({ competitionYear, teamFeatureEnabled }),
    })
  }

  async setMaxTeamSize(competitionYear: string, maxTeamSize: number) {
    return this.request<{ competitionYear: string; teamFeatureEnabled: boolean; maxTeamSize: number; updatedAt: string; updatedBy: string }>('/admin/teams/settings/max-team-size', {
      method: 'PUT',
      body: JSON.stringify({ competitionYear, maxTeamSize }),
    })
  }

  // Pilot - Team endpoints
  async getMyTeam() {
    return this.request<{ teamFeatureEnabled: boolean; assigned?: boolean; team?: { teamId: string; teamName: string; competitionYear: string }; members?: Array<{ userId: string; firstName: string; lastName: string; fullName: string; gender: string; paragliderManufacturer: string; paragliderModel: string; faiNation: string; photoUrl: string | null }> }>('/teams/me', {
      method: 'GET',
    })
  }

  async listMyTeamMembers() {
    return this.request<{ members: Array<{ userId: string; firstName: string; lastName: string; fullName: string; gender: string; paragliderManufacturer: string; paragliderModel: string; faiNation: string; photoUrl: string | null }> }>('/teams/me/members', {
      method: 'GET',
    })
  }

}

export const apiService = new ApiService()
export default apiService
